import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, Badge, Button, Icon, Divider, Input, Field } from '../../../components/ui';
import { useWallet } from '../../../context/WalletContext';
import orderImportService, {
  ImportCandidate,
  ImportParseResult,
  BatchOrderInput,
  BatchSubmitResult,
} from '../../../services/orderImportService';
import { useBatchRates, pickRate } from '../../orderImport/useBatchRates';

interface CsvBatchCardProps {
  result: ImportParseResult;
  onDiscard: () => void;
}

const readDefaultShipping = (): { id: string; name: string } => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return { id: user?.default_shipping_id || '', name: user?.default_shipping_name || '' };
  } catch {
    return { id: '', name: '' };
  }
};

const recipientName = (a: Record<string, any>): string => a?.contactName || a?.companyName || 'Unknown recipient';

const destinationLine = (a: Record<string, any>): string => {
  const city = a?.city?.name || a?.city || '';
  const country = a?.country?.name || a?.countryCode || '';
  return [city, country].filter(Boolean).join(', ') || '—';
};

const round2 = (n: number) => Math.round(n * 100) / 100;

const CsvBatchCard: React.FC<CsvBatchCardProps> = ({ result, onDiscard }) => {
  const { balance } = useWallet();
  const { rowRates, isFetching, fetchAll } = useBatchRates();

  const initialSender = useMemo(readDefaultShipping, []);
  const [senderName, setSenderName] = useState(initialSender.name);
  const [senderId, setSenderId] = useState(initialSender.id);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitResult, setSubmitResult] = useState<BatchSubmitResult | null>(null);
  const [submittedRows, setSubmittedRows] = useState<ImportCandidate[]>([]);

  useEffect(() => {
    fetchAll(result.orders);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result]);

  const excludedRows = useMemo(() => result.orders.filter((c) => c.errors.length > 0), [result.orders]);
  const validRows = useMemo(() => result.orders.filter((c) => c.errors.length === 0), [result.orders]);

  const cheapestByRow = useMemo(() => {
    const map: Record<number, ReturnType<typeof pickRate>> = {};
    validRows.forEach((c) => {
      const state = rowRates[c.rowNumber];
      if (state?.status === 'done' && state.rates.length) {
        map[c.rowNumber] = pickRate(state.rates, 'cheapest');
      }
    });
    return map;
  }, [validRows, rowRates]);

  const readyRows = useMemo(() => validRows.filter((c) => cheapestByRow[c.rowNumber]), [validRows, cheapestByRow]);
  const batchCurrency = readyRows[0] ? cheapestByRow[readyRows[0].rowNumber]!.currency : 'USD';
  const batchTotal = readyRows.reduce((sum, c) => {
    const rate = cheapestByRow[c.rowNumber]!;
    return sum + rate.price + (c.customs.insuranceAmount || 0);
  }, 0);

  const canConfirm =
    !isFetching && !submitting && readyRows.length > 0 && !!senderName.trim() && !!senderId.trim();

  const handleConfirm = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const orders: BatchOrderInput[] = readyRows.map((c) => {
        const rate = cheapestByRow[c.rowNumber]!;
        const shipping = round2(rate.price);
        const insurance = round2(c.customs.insuranceAmount || 0);
        return {
          save_as_draft: false,
          addresses: c.addresses,
          parcels: c.parcels,
          customs: c.customs,
          isDocument: c.isDocument,
          currency: rate.currency || c.currency,
          referenceNumber: c.reference,
          selectedRate: {
            id: rate.id,
            name: rate.name,
            provider: rate.provider,
            price: shipping,
            base_price: round2(rate.base_price),
            has_commission: rate.has_commission,
            currency: rate.currency,
            deliveryTime: rate.transit_time,
          },
          price: { shipping, insurance, tax: 0, total: round2(shipping + insurance) },
          carrier: rate.provider,
          service: rate.name,
          deliveryEstimate: rate.estimated_delivery_date ?? null,
        };
      });

      const res = await orderImportService.submitBatch({
        paymentMethod: 'wallet',
        default_shipping_id: senderId.trim(),
        default_shipping_name: senderName.trim(),
        orders,
      });
      setSubmitResult(res);
      setSubmittedRows(readyRows);
    } catch (err: any) {
      setSubmitError(err?.response?.data?.message || err?.message || 'Failed to create the batch. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitResult) {
    const failedRowFor = (index: number) => submittedRows[index];
    return (
      <Card pad style={{ maxWidth: 460, margin: '8px 0', border: '1px solid var(--ok)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <Icon name="checkCircle" size={16} style={{ color: 'var(--ok)' }} />
          <strong style={{ color: 'var(--ok)' }}>Batch processed</strong>
        </div>
        <p className="muted" style={{ fontSize: 13, marginBottom: 12 }}>
          {submitResult.submitted.length} order{submitResult.submitted.length === 1 ? '' : 's'} created
          {submitResult.payment
            ? ` · charged ${submitResult.payment.total.toFixed(2)} ${submitResult.payment.currency}`
            : ''}
          .
        </p>
        {submitResult.submitted.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
            {submitResult.submitted.map((o: any, i: number) => (
              <Link
                key={o.id ?? i}
                to={`/orders/${o.id}`}
                style={{ fontSize: 13, color: 'var(--brand-blue)', fontWeight: 600 }}
              >
                Order #{o.order_number || o.id} →
              </Link>
            ))}
          </div>
        )}
        {submitResult.failures.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <Badge tone="err" dot style={{ marginBottom: 6 }}>
              {submitResult.failures.length} row{submitResult.failures.length === 1 ? '' : 's'} failed
            </Badge>
            {submitResult.failures.map((f, i) => (
              <div key={i} className="muted" style={{ fontSize: 12 }}>
                {failedRowFor(f.index)?.reference || `Row ${f.index + 1}`}: {f.error}
              </div>
            ))}
          </div>
        )}
        <Button variant="secondary" onClick={onDiscard}>
          Close
        </Button>
      </Card>
    );
  }

  return (
    <Card pad style={{ maxWidth: 460, margin: '8px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <Icon name="paperclip" size={16} style={{ color: 'var(--brand-blue)' }} />
        <strong>CSV batch · {result.orders.length} row{result.orders.length === 1 ? '' : 's'}</strong>
      </div>
      <p className="muted" style={{ fontSize: 12, marginBottom: 12 }}>
        Rates auto-picked cheapest per row. Review below, then confirm to create all at once.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
        {validRows.map((c) => {
          const state = rowRates[c.rowNumber];
          const rate = cheapestByRow[c.rowNumber];
          return (
            <div
              key={c.rowNumber}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '8px 10px',
                borderRadius: 8,
                background: 'var(--bg-tint)',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {recipientName(c.addresses.to)}
                </div>
                <div className="muted" style={{ fontSize: 11 }}>
                  {destinationLine(c.addresses.to)} · {c.parcels[0]?.weight ?? 0} kg
                </div>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                {state?.status === 'loading' || !state ? (
                  <span className="muted" style={{ fontSize: 12 }}>Fetching…</span>
                ) : state.status === 'error' || !rate ? (
                  <Badge tone="err" style={{ fontSize: 11 }}>No rate</Badge>
                ) : (
                  <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--brand-blue)' }}>
                    {rate.price.toFixed(2)} {rate.currency}
                  </span>
                )}
              </div>
            </div>
          );
        })}

        {excludedRows.map((c) => (
          <div
            key={c.rowNumber}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '8px 10px',
              borderRadius: 8,
              border: '1px dashed var(--line)',
              opacity: 0.7,
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{c.reference || `Row ${c.rowNumber}`}</div>
              <div style={{ fontSize: 11, color: 'var(--warn)' }}>{c.errors.join(' ')}</div>
            </div>
            <Badge tone="warn" style={{ fontSize: 11, flexShrink: 0 }}>Excluded</Badge>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <Field label="Sender name" style={{ flex: 1, margin: 0 }}>
          <Input
            value={senderName}
            onChange={(e) => setSenderName(e.target.value)}
            placeholder="Sender name"
            style={{ fontSize: 12, padding: '6px 8px' }}
          />
        </Field>
        <Field label="Sender ID / Tax number" style={{ flex: 1, margin: 0 }}>
          <Input
            value={senderId}
            onChange={(e) => setSenderId(e.target.value)}
            placeholder="Sender ID"
            style={{ fontSize: 12, padding: '6px 8px' }}
          />
        </Field>
      </div>

      <Divider style={{ marginBottom: 12 }} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <span className="muted" style={{ fontSize: 12 }}>
          {readyRows.length} of {validRows.length} ready · wallet balance {Number(balance).toFixed(2)}
        </span>
        <strong style={{ fontSize: 15 }}>
          {batchTotal.toFixed(2)} {batchCurrency}
        </strong>
      </div>

      {submitError && (
        <div style={{ fontSize: 12, color: 'var(--warn)', marginBottom: 8 }}>{submitError}</div>
      )}

      <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
        <Button variant="ghost" style={{ flex: 1 }} onClick={onDiscard} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="primary" style={{ flex: 1 }} onClick={handleConfirm} disabled={!canConfirm}>
          {submitting
            ? 'Creating…'
            : isFetching
              ? 'Fetching rates…'
              : `Confirm & create ${readyRows.length} order${readyRows.length === 1 ? '' : 's'}`}
        </Button>
      </div>
    </Card>
  );
};

export default CsvBatchCard;
